import React, { useEffect, useMemo, useState } from 'react';

import { AssetAllocationCategory } from '@/cip/_openapi/portfolio-optimizer';
import { ConfirmModal } from '@/components/ConfirmModal';
import { Spinner } from '@/components/Spinner';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
    formatRatio,
    getAssetAllocationCategoryLabel,
    isValidOverridePercent,
    isValidOverrideSum,
    MAX_OVERRIDE_PERCENT,
    MIN_OVERRIDE_PERCENT,
} from 'utils/asset-allocation-utils';

import { useDeleteAssetAllocationOverride, useSetAssetAllocationOverride } from './asset-allocation-queries';

interface CategoryRow {
    category: AssetAllocationCategory;
    morningstar: number | null;
    override: number | null;
    value: number | null;
}

interface ManageOverridesDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    sId: string;
    title: string;
    rows: CategoryRow[];
    hasOverrides: boolean;
}

const OVERRIDE_INPUT_REGEX = /^-?\d{0,3}[.,]?\d{0,2}$/;

const formatInput = (value: number | null): string =>
    value === null ? '' : (Math.round(value * 1e4) / 1e2).toString();

const parseInput = (raw: string): number | null => {
    const trimmed = raw.trim().replace(',', '.');
    if (trimmed === '' || trimmed === '-') return null;
    const num = Number(trimmed);
    if (!Number.isFinite(num)) return null;
    return num / 100;
};

const buildInitialInputs = (rows: CategoryRow[]): Record<string, string> =>
    Object.fromEntries(rows.map((row) => [row.category, formatInput(row.override)]));

export const ManageOverridesDialog: React.FC<ManageOverridesDialogProps> = ({
    open,
    onOpenChange,
    sId,
    title,
    rows,
    hasOverrides,
}) => {
    const [inputs, setInputs] = useState<Record<string, string>>(() => buildInitialInputs(rows));
    const [confirmClearOpen, setConfirmClearOpen] = useState(false);

    const setMutation = useSetAssetAllocationOverride();
    const deleteMutation = useDeleteAssetAllocationOverride();

    // Don't depend on `rows`: a background refetch would wipe in-progress edits.
    useEffect(() => {
        if (open) setInputs(buildInitialInputs(rows));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, sId]);

    // Blank inputs submit as 0 (not Morningstar).
    const rowState = useMemo(() => {
        return rows.map((row) => {
            const userValue = parseInput(inputs[row.category] ?? '');
            const effective = userValue ?? 0;
            const outOfRange = userValue !== null && !isValidOverridePercent(userValue * 100);
            return { effective, outOfRange };
        });
    }, [inputs, rows]);

    const anyOutOfRange = rowState.some((r) => r.outOfRange);
    const sum = rowState.reduce<number>((acc, r) => acc + r.effective, 0);
    const sumValid = isValidOverrideSum(sum);
    const canSave = !anyOutOfRange && sumValid;

    const handleSave = () => {
        if (!canSave) return;
        setMutation
            .mutateAsync({
                sId,
                overrides: rows.map((row, i) => ({
                    category: row.category,
                    value: rowState[i].effective,
                })),
            })
            .then(() => onOpenChange(false))
            .catch(() => {});
    };

    const handleClear = () => {
        deleteMutation
            .mutateAsync({ sId })
            .then(() => {
                setConfirmClearOpen(false);
                onOpenChange(false);
            })
            .catch(() => {});
    };

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-xl">
                    {setMutation.isPending && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-white/70">
                            <Spinner showText text="Saving… this may take a moment." />
                        </div>
                    )}
                    <DialogHeader>
                        <DialogTitle>Asset Allocation – {title}</DialogTitle>
                        <DialogDescription>
                            Override Morningstar asset class ratios. Values must sum to 100%.
                        </DialogDescription>
                    </DialogHeader>
                    <div>
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-gray-50">
                                    <TableHead className="text-xs font-medium text-gray-500">Category</TableHead>
                                    <TableHead className="text-right text-xs font-medium text-gray-500">
                                        Morningstar
                                    </TableHead>
                                    <TableHead className="text-right text-xs font-medium text-gray-500">
                                        Override (%)
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.map((row, i) => (
                                    <TableRow key={row.category}>
                                        <TableCell>{getAssetAllocationCategoryLabel(row.category)}</TableCell>
                                        <TableCell className="text-right tabular-nums">
                                            {formatRatio(row.morningstar)}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Input
                                                type="text"
                                                inputMode="decimal"
                                                className={`ml-auto w-32 text-right tabular-nums ${
                                                    rowState[i].outOfRange
                                                        ? 'border-red-500 focus-visible:ring-red-500'
                                                        : ''
                                                }`}
                                                value={inputs[row.category] ?? ''}
                                                onChange={(e) => {
                                                    const next = e.target.value;
                                                    if (next === '' || OVERRIDE_INPUT_REGEX.test(next)) {
                                                        setInputs((prev) => ({ ...prev, [row.category]: next }));
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        <div className="mt-3 flex items-center justify-between text-sm">
                            <div className={canSave ? 'text-gray-600' : 'text-red-600'}>
                                {anyOutOfRange
                                    ? `Each value must be between ${MIN_OVERRIDE_PERCENT}% and ${MAX_OVERRIDE_PERCENT}%.`
                                    : sumValid
                                      ? `Total: ${formatRatio(sum)} · blank rows are saved as 0`
                                      : `Total: ${formatRatio(sum)} — must equal 100%`}
                            </div>
                        </div>
                    </div>
                    <DialogFooter className="flex justify-between sm:justify-between">
                        <div>
                            {hasOverrides && (
                                <Button
                                    variant="outline"
                                    onClick={() => setConfirmClearOpen(true)}
                                    disabled={deleteMutation.isPending || setMutation.isPending}
                                >
                                    Clear overrides
                                </Button>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                disabled={setMutation.isPending}
                            >
                                Cancel
                            </Button>
                            <Button onClick={handleSave} disabled={!canSave || setMutation.isPending}>
                                {setMutation.isPending ? (
                                    <span className="flex items-center gap-2">
                                        <Spinner size="sm" className="border-white" />
                                        Saving…
                                    </span>
                                ) : (
                                    'Save'
                                )}
                            </Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ConfirmModal
                open={confirmClearOpen}
                onOpenChange={setConfirmClearOpen}
                onConfirm={handleClear}
                title="Clear asset allocation overrides"
                description={`Remove all manual overrides for ${title}? Morningstar values will become authoritative again.`}
                confirmText="Clear overrides"
                confirmVariant="destructive"
                loading={deleteMutation.isPending}
                closeOnConfirm={false}
            />
        </>
    );
};
